import { useState, useEffect } from "react";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/query-client";
import { EmailComposer } from "@/components/email/email-composer";
import type { ClaimQuickPickContact } from "@/components/email/contact-quick-picks";
import { ClaimAIEmailDraftModal } from "@/components/claims/modals/claim-ai-email-draft-modal";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";

/**
 * Standalone page rendered inside the email composer popup window.
 *
 * Reads initialisation props from:
 *   - URL search params  (primitives: claimId, claimNumber, etc.)
 *   - sessionStorage     (objects/arrays: claimContacts, claimDocuments, replyTo, forwardEmail, draft)
 *
 * Gmail > Drafts > Edit only passes claimId + draft, so when the session has
 * no contacts this page loads GET /api/claims/:id/contacts (same query key as
 * useClaimData). A 403 or any other failure shows no buttons and no toast.
 *
 * On successful send, posts { type: "email-sent", claimId? } to the opener
 * so the parent window can invalidate its React Query caches.
 */
export function EmailComposerPopupPage() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <EmailComposerPopupContent />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function parsePopupSession(): Record<string, any> {
  const params = new URLSearchParams(window.location.search);
  const sessionKey = params.get("session_key") ?? "";
  const sessionRaw = sessionKey ? sessionStorage.getItem(sessionKey) : null;
  return sessionRaw ? JSON.parse(sessionRaw) : {};
}

function EmailComposerPopupContent() {
  const params = new URLSearchParams(window.location.search);

  const claimId = params.get("claim_id") ?? undefined;
  const claimNumber = params.get("claim_number") ?? undefined;
  const insuranceCarrier = params.get("insurance_carrier") ?? undefined;
  const defaultTo = params.get("default_to") ?? undefined;
  const defaultSubject = params.get("default_subject") ?? undefined;

  // Parse once so draft / replyTo / forwardEmail stay referentially stable.
  // Re-parsing on every render would re-trigger EmailComposer's init effect
  // when the contacts query settles and wipe in-progress edits.
  const [session] = useState(parsePopupSession);

  const sessionContacts: ClaimQuickPickContact[] = Array.isArray(session.claimContacts)
    ? session.claimContacts
    : [];
  const hasSessionContacts = sessionContacts.length > 0;

  const { data: fetchedContacts } = useQuery<ClaimQuickPickContact[]>({
    queryKey: [`/api/claims/${claimId}/contacts`],
    enabled: Boolean(claimId && !hasSessionContacts),
  });

  const claimContacts = hasSessionContacts
    ? sessionContacts
    : (fetchedContacts ?? []);
  const claimDocuments: any[] | undefined = session.claimDocuments;
  const replyTo = session.replyTo;
  const forwardEmail = session.forwardEmail;
  const draft = session.draft ?? null;

  // AI draft modal state (self-contained in popup per spec)
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiDraftSubject, setAiDraftSubject] = useState("");
  const [aiDraftBody, setAiDraftBody] = useState("");
  const [currentEmailBody, setCurrentEmailBody] = useState("");

  // The composer is always open in the popup — if the user closes it, we close the window.
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    if (!isOpen) {
      window.close();
    }
  }, [isOpen]);

  function handleSent() {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(
        { type: "email-sent", claimId },
        window.location.origin
      );
    }
    window.close();
  }

  function handleClose() {
    setIsOpen(false);
  }

  return (
    <>
      {/* EmailComposer renders inline (fill-window) — no portal, no drag handle */}
      <EmailComposer
        isOpen={isOpen}
        onClose={handleClose}
        isPopupMode={true}
        claimId={claimId}
        claimNumber={claimNumber}
        insuranceCarrier={insuranceCarrier}
        claimContacts={claimContacts}
        claimDocuments={claimDocuments}
        defaultTo={defaultTo}
        defaultSubject={defaultSubject}
        replyTo={replyTo}
        forwardEmail={forwardEmail}
        draft={draft}
        onSent={handleSent}
        onOpenAIDraft={claimId ? () => setShowAIModal(true) : undefined}
        aiDraftSubject={aiDraftSubject}
        aiDraftBody={aiDraftBody}
        onBodyChange={setCurrentEmailBody}
      />

      {/* AI Draft Modal — self-contained inside the popup */}
      {claimId && (
        <ClaimAIEmailDraftModal
          open={showAIModal}
          onOpenChange={setShowAIModal}
          claimId={claimId}
          currentEmailBody={currentEmailBody}
          onDraftGenerated={(subject, body) => {
            setAiDraftSubject(subject);
            setAiDraftBody(body);
            setShowAIModal(false);
          }}
        />
      )}
    </>
  );
}
